import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getTyre, type TyreDetail } from '../tyres.api';

export type TyreDetailState =
  { status: 'loading' } | { status: 'loaded'; tyre: TyreDetail } | { status: 'not_found' } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TyreDetailState {
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** One tyre with its fitments and history; `reload` after a move keeps the old page up until the new one lands. */
export function useTyreDetail(id: string) {
  const [state, setState] = useState<TyreDetailState>({ status: 'loading' });

  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', tyre: await getTyre(id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    getTyre(id)
      .then((tyre) => !cancelled && setState({ status: 'loaded', tyre }))
      .catch((err) => !cancelled && setState(toErrorState(err)));
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { state, reload };
}
